const MAX_PUT_ATTEMPTS = 3;
const GCS_PUT_TIMEOUT_MS = 30_000;

interface TransientError extends Error {
  transient?: boolean;
}

export type PutProgressCallback = (bytesLoaded: number, bytesTotal: number) => void;

function putToGcsOnce(
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress?: PutProgressCallback,
  signal?: AbortSignal,
  timeoutMs = GCS_PUT_TIMEOUT_MS,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.timeout = timeoutMs;

    // GCS signed URLs are signed for a specific Content-Type.
    // Applying put_headers verbatim avoids 403 SignatureDoesNotMatch.
    // Never add Authorization — signed URLs require no auth.
    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value);
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(e.loaded, e.total);
      }
    };

    function abortXhr() {
      xhr.abort();
    }

    function settle(fn: () => void) {
      signal?.removeEventListener("abort", abortXhr);
      fn();
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(file.size, file.size);
        settle(resolve);
      } else {
        const err = Object.assign(new Error(`GCS PUT failed: ${xhr.status}`), { transient: xhr.status >= 500 }) as TransientError;
        settle(() => reject(err));
      }
    };

    xhr.onerror = () => {
      const err = Object.assign(new Error("GCS PUT network error"), { transient: true }) as TransientError;
      settle(() => reject(err));
    };

    xhr.ontimeout = () => {
      const err = Object.assign(new Error("GCS PUT timed out"), { transient: true }) as TransientError;
      settle(() => reject(err));
    };

    xhr.onabort = () => {
      settle(() => reject(new DOMException("Aborted", "AbortError")));
    };

    signal?.addEventListener("abort", abortXhr);
    xhr.send(file);
  });
}

export async function putToGcs(
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress?: PutProgressCallback,
  options?: { maxAttempts?: number; signal?: AbortSignal; timeoutMs?: number }
): Promise<void> {
  const maxAttempts = options?.maxAttempts ?? MAX_PUT_ATTEMPTS;
  const { signal, timeoutMs } = options ?? {};

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    try {
      await putToGcsOnce(url, file, headers, onProgress, signal, timeoutMs);
      return;
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      const transient = (err as TransientError).transient;
      if (!transient || attempt === maxAttempts) {
        console.error(
          `[upload] GCS PUT failed for "${file.name}" after ${attempt} attempt(s):`,
          err
        );
        throw err;
      }
      // Reset progress so the UI doesn't stay pinned at the pre-failure mark.
      onProgress?.(0, file.size);
      // Exponential backoff with jitter: ~500ms, ~1500ms. Interruptible by abort signal.
      const delay = 500 * 2 ** (attempt - 1) + Math.random() * 250;
      await new Promise<void>((r, rj) => {
        const onAbort = () => { clearTimeout(t); rj(new DOMException("Aborted", "AbortError")); };
        const t = setTimeout(() => { signal?.removeEventListener("abort", onAbort); r(); }, delay);
        signal?.addEventListener("abort", onAbort);
      });
    }
  }
}
